import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, X, Check } from "lucide-react";

export function BeforeAfter({ te }) {
  const ba = te.before_after;
  return (
    <section className="py-12 lg:py-28 bg-white border-y border-[#EAE5DB]" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 lg:mb-16">
          <div className="overline mb-3">{ba.sub}</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#292524] leading-tight">
            {ba.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 items-stretch">
          {/* BEFORE */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
            data-testid="before-card"
          >
            <div className="overline text-[#D9423E] mb-3 ml-1">{ba.before_label}</div>
            <div className="cv-mini bad">
              <div className="cv-score-pill bad">{ba.before_score}</div>

              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-[#F2EBE1] flex items-center justify-center flex-shrink-0 border-2 border-[#D9423E]">
                  <X className="w-6 h-6 text-[#D9423E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="cv-mini-name">Jean Dupont</div>
                  <div className="cv-mini-meta">28 ans · Célibataire · Français</div>
                </div>
              </div>
              <div className="cv-anno bad" style={{ marginLeft: 0 }}>
                <X className="w-3 h-3" /> {ba.annos.photo}
              </div>
              <div className="cv-anno bad" style={{ marginLeft: 6 }}>
                <X className="w-3 h-3" /> {ba.annos.no_visa}
              </div>

              <div className="cv-mini-section bad-sec">EXPÉRIENCE</div>
              <div className="cv-mini-line">
                <span className="cv-mini-bad-text">Chauffeur PL — France · Fév 2026 — Fév 2026</span>
                <span className="cv-anno bad"><X className="w-3 h-3" /> {ba.annos.bad_dates}</span>
              </div>
              <div className="cv-mini-line">
                <span className="cv-mini-bad-text">Permis PL · Mécanicien poids lourd</span>
                <span className="cv-anno bad"><X className="w-3 h-3" /> {ba.annos.french_terms}</span>
              </div>

              <div className="cv-mini-section bad-sec">COMPÉTENCES</div>
              <div className="cv-mini-line">
                <span className="cv-mini-bad-text">Conduite · Mécanique · Sérieux</span>
                <span className="cv-anno bad"><X className="w-3 h-3" /> {ba.annos.no_keywords}</span>
              </div>

              <div className="cv-mini-section bad-sec">LANGUES</div>
              <div className="cv-mini-line cv-mini-bad-text">Français (natif) · Anglais (notions)</div>
            </div>
          </motion.div>

          {/* Arrow */}
          <div className="flex items-center justify-center py-4 lg:py-0">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3, type: "spring" }}
              className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-[#D95D39] flex items-center justify-center shadow-2xl rotate-90 lg:rotate-0"
            >
              <ArrowRight className="w-8 h-8 text-white" strokeWidth={3} />
            </motion.div>
          </div>

          {/* AFTER */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
            data-testid="after-card"
          >
            <div className="overline text-[#3B8268] mb-3 ml-1">{ba.after_label}</div>
            <div className="cv-mini good">
              <div className="cv-score-pill good">{ba.after_score}</div>

              <div className="cv-mini-name">JEAN DUPONT</div>
              <div className="cv-mini-meta">
                FIFO Bus Driver · HR Licence · PTD Authorised
                <br />Karratha, WA 6714 · +61 4XX XXX XXX
                <br /><strong>Visa: 417 WHV — Full work rights</strong>
                <span className="cv-anno good"><Check className="w-3 h-3" /> {ba.annos.visa_ok}</span>
              </div>

              <div className="cv-mini-section good-sec">PROFESSIONAL EXPERIENCE</div>
              <div className="cv-mini-line">
                <strong>FIFO Bus Driver — Gold Rush Tour, Kalgoorlie WA</strong>
              </div>
              <div className="cv-mini-line" style={{ color: "var(--text-3)", fontStyle: "italic", fontSize: "10.5px" }}>
                Jan 2024 – May 2025 (17 months)
                <span className="cv-anno good"><Check className="w-3 h-3" /> {ba.annos.good_dates}</span>
              </div>
              <div className="cv-mini-line">• Operated 55-seater coaches transporting workers to KCGM</div>
              <div className="cv-mini-line">• HR Licence Manual, PTD, White Card, KCGM Induction
                <span className="cv-anno good"><Check className="w-3 h-3" /> {ba.annos.au_terms}</span>
              </div>

              <div className="cv-mini-section good-sec">KEY SKILLS</div>
              <div className="cv-mini-line">FIFO · 2/1 roster · Heavy Vehicle · Mine-site</div>
              <div className="cv-mini-line" style={{ marginTop: 4 }}>
                <span className="cv-anno good" style={{ marginLeft: 0 }}><Check className="w-3 h-3" /> {ba.annos.keywords_ok}</span>
                <span className="cv-anno good"><Check className="w-3 h-3" /> {ba.annos.local_ok}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
